/**
 * fr3Doc — the designer's fr3 DOCUMENT backend (design artifact §5.1).
 *
 * Unlike the ZPL document (element model = source of truth, ZPL compiled),
 * an .fr3 file is ALSO read by the real FastReport engine in MySales /
 * MyFashionManager, so the XML itself stays the source of truth and edits are
 * SURGICAL DOM MUTATIONS: only attributes the user actually changed are
 * written; scripts, datasets, print settings and every unknown attribute pass
 * through untouched. Deleting an element removes exactly that node.
 *
 * Serialisation notes:
 *  - the original XML prolog (with its declared encoding) is preserved;
 *  - all non-ASCII characters are emitted as numeric character references,
 *    so the byte stream is plain ASCII and stays valid under ANY declared
 *    8-bit encoding (windows-1252 legacy templates included);
 *  - FastReport writes locale decimals ("3,77953…") — the separator is
 *    detected from the source and reused for every number we write.
 *
 * Editing scope: move / resize / edit text / font size / alignment; deletion
 * only for flat LABEL templates (the invoice touch-up mode keeps structure).
 * Adding new objects is intentionally not supported yet.
 *
 * <inherited ParentReport="…"> templates are refused — the parent form is
 * invisible here (the Android renderer refuses the same files).
 */

import { nextId } from './zplModel';

const FR_UNITS_PER_INCH = 96.0;

function unitsToMm(units) {
    return Math.round((units / FR_UNITS_PER_INCH) * 25.4 * 100) / 100;
}

function mmToUnits(mm) {
    return (mm / 25.4) * FR_UNITS_PER_INCH;
}

function mmToUnitsFromDots(dots, dpi) {
    return (dots * FR_UNITS_PER_INCH) / dpi;
}

function attrD(el, name, def) {
    const v = el.getAttribute(name);
    if (v === null || v === '') return def;
    const parsed = parseFloat(String(v).replace(',', '.'));
    return isNaN(parsed) ? def : parsed;
}

function attr(el, name, def) {
    const v = el.getAttribute(name);
    return v === null ? def : v;
}

const BAND_TAGS = {
    TfrxReportTitle: 'Report title', TfrxPageHeader: 'Page header', TfrxHeader: 'Header',
    TfrxMasterData: 'Master data', TfrxDetailData: 'Detail data', TfrxChild: 'Child',
    TfrxFooter: 'Footer', TfrxPageFooter: 'Page footer', TfrxSummary: 'Summary',
    TfrxGroupHeader: 'Group header', TfrxGroupFooter: 'Group footer',
    TfrxColumnHeader: 'Column header', TfrxColumnFooter: 'Column footer',
    TfrxOverlay: 'Overlay',
};

/**
 * Open an .fr3 for editing.
 *
 * @returns { ok, handle, model, lanes, isFlat, warnings } or { ok:false, reason }
 *   handle: opaque state for applyEdits/serialize (doc, node map, separators)
 *   model:  { widthMm, heightMm, dpmm, elements } for the canvas
 *   lanes:  [{ name, topMm, heightMm }] band strips (invoice rendering)
 *   isFlat: no repeating data bands with height -> label-style template
 */
export function openFr3(xmlText, dpmm) {
    const d = dpmm || 8;
    const dpi = Math.round(d * 25.4);

    let doc;
    try {
        doc = new DOMParser().parseFromString(xmlText, 'text/xml');
    } catch (e) {
        return { ok: false, reason: 'XML parse failed' };
    }
    if (!doc || !doc.documentElement) return { ok: false, reason: 'XML parse failed' };
    if (doc.getElementsByTagName('parsererror').length > 0) {
        return { ok: false, reason: 'Not well-formed XML' };
    }

    const root = doc.documentElement;
    if (root.nodeName === 'inherited' || root.getAttribute('ParentReport')) {
        return { ok: false, reason: 'inherited' };
    }
    if (root.nodeName !== 'TfrxReport') {
        return { ok: false, reason: 'Not a FastReport .fr3 document' };
    }

    // Decimal separator convention of THIS file (Delphi writes locale commas).
    const decimalSep = /="\d+,\d+"/.test(xmlText) ? ',' : '.';
    const prologMatch = xmlText.match(/^\s*<\?xml[^>]*\?>/);
    const prolog = prologMatch ? prologMatch[0].trim() : '<?xml version="1.0" encoding="utf-8"?>';

    const model = { widthMm: 50, heightMm: 25, dpmm: d, elements: [] };
    const lanes = [];
    const nodes = {};
    const warnings = [];

    let leftMarginUnits = 0;
    let topMarginUnits = 0;
    // Stacked band positions: the .fr3 stores DESIGNER coordinates, where
    // each band's Top includes the designer's band-caption chrome — printing
    // stacks the bands compactly from the top instead. Displaying raw Tops
    // pushes everything down and clips the bottom, so the editor mirrors the
    // print engine: bands sorted by design Top, stacked by their heights.
    // The same stacked origin is stored per node, so the write-back
    // (absolute mm -> band-relative units) round-trips exactly.
    const bandStackTops = new Map();
    const pages = root.getElementsByTagName('TfrxReportPage');
    if (pages.length > 0) {
        const page = pages[0];
        const pw = attrD(page, 'PaperWidth', 0);
        const ph = attrD(page, 'PaperHeight', 0);
        if (pw > 0) model.widthMm = Math.round(pw * 100) / 100;
        if (ph > 0) model.heightMm = Math.round(ph * 100) / 100;
        leftMarginUnits = mmToUnits(attrD(page, 'LeftMargin', 0));
        topMarginUnits = mmToUnits(attrD(page, 'TopMargin', 0));
        if (pages.length > 1) warnings.push('Multi-page template — only page 1 is editable here');

        const bandNodes = [];
        for (let c = page.firstChild; c; c = c.nextSibling) {
            if (c.nodeType === 1 && BAND_TAGS[c.nodeName]) bandNodes.push(c);
        }
        bandNodes.sort((a, b) => attrD(a, 'Top', 0) - attrD(b, 'Top', 0));
        // Page footers print at the page BOTTOM regardless of design order.
        const paperHeightUnits = mmToUnits(ph > 0 ? ph : 297);
        const bottomMarginUnits = mmToUnits(attrD(page, 'BottomMargin', 0));
        let stack = 0;
        bandNodes.forEach((band) => {
            if (band.nodeName === 'TfrxPageFooter') {
                bandStackTops.set(band,
                    paperHeightUnits - bottomMarginUnits - topMarginUnits - attrD(band, 'Height', 0));
                return;
            }
            bandStackTops.set(band, stack);
            stack += attrD(band, 'Height', 0);
        });
    }

    let dataBands = 0;

    const register = (node, type, bandLeft, bandTop, extra) => {
        const id = nextId();
        const originLeft = leftMarginUnits + bandLeft;
        const originTop = topMarginUnits + bandTop;
        const leftU = attrD(node, 'Left', 0);
        const topU = attrD(node, 'Top', 0);
        const widthU = attrD(node, 'Width', 0);
        const heightU = attrD(node, 'Height', 0);
        const el = Object.assign({
            id, type,
            x: unitsToMm(originLeft + leftU),
            y: unitsToMm(originTop + topU),
            w: unitsToMm(widthU),
            h: unitsToMm(heightU),
        }, extra);
        nodes[id] = {
            node,
            originLeft, originTop,
            orig: {
                leftU, topU, widthU, heightU,
                text: attr(node, 'Text', null),
                fontU: Math.abs(attrD(node, 'Font.Height', -11)),
                fontSign: attrD(node, 'Font.Height', -11) < 0 ? -1 : 1,
                hAlign: attr(node, 'HAlign', ''),
            },
        };
        model.elements.push(el);
        return el;
    };

    const walk = (node, bandLeft, bandTop) => {
        for (let child = node.firstChild; child; child = child.nextSibling) {
            if (child.nodeType !== 1) continue;
            const tag = child.nodeName;

            if (BAND_TAGS[tag]) {
                // Stacked (print-order) position, not the designer's raw Top.
                const bTop = bandStackTops.has(child) ? bandStackTops.get(child) : attrD(child, 'Top', 0);
                const bHeight = attrD(child, 'Height', 0);
                lanes.push({
                    name: BAND_TAGS[tag] + (child.getAttribute('Name') ? ' · ' + child.getAttribute('Name') : ''),
                    topMm: unitsToMm(topMarginUnits + bTop),
                    heightMm: unitsToMm(bHeight),
                });
                if (tag === 'TfrxMasterData' || tag === 'TfrxDetailData') dataBands += 1;
                walk(child, attrD(child, 'Left', 0), bTop);
                continue;
            }

            if (tag === 'TfrxMemoView') {
                const fontU = Math.abs(attrD(child, 'Font.Height', -11));
                const hAlign = attr(child, 'HAlign', 'haLeft');
                register(child, 'text', bandLeft, bandTop, {
                    text: attr(child, 'Text', ''),
                    font: Math.max(6, Math.round((fontU * dpi) / FR_UNITS_PER_INCH)),
                    fontW: Math.max(6, Math.round((fontU * dpi) / FR_UNITS_PER_INCH)),
                    align: hAlign === 'haCenter' ? 'C' : (hAlign === 'haRight' ? 'R' : (hAlign === 'haBlock' ? 'J' : 'L')),
                    maxLines: 3,
                    rotation: 'N',
                    background: attr(child, 'Color', '') !== '',
                });
            } else if (tag === 'TfrxBarCodeView') {
                const barType = attr(child, 'BarType', 'bcCode128');
                register(child, 'barcode', bandLeft, bandTop, {
                    data: attr(child, 'Expression', ''),
                    barType: barType.indexOf('EAN13') >= 0 ? 'EAN13'
                        : (barType.indexOf('EAN8') >= 0 ? 'EAN8'
                            : (barType.indexOf('39') >= 0 ? 'CODE39' : 'CODE128')),
                    interpretation: String(attr(child, 'ShowText', 'True')).toLowerCase() !== 'false',
                    module: 2,
                    rotation: 'N',
                });
            } else if (tag === 'TfrxShapeView' || tag === 'TfrxLineView') {
                register(child, 'box', bandLeft, bandTop, {
                    thickness: Math.max(1, Math.round((attrD(child, 'Frame.Width', 1) * dpi) / FR_UNITS_PER_INCH)),
                    fill: 'none',
                });
            } else if (tag === 'TfrxPictureView') {
                register(child, 'logo', bandLeft, bandTop, {});
            } else {
                walk(child, bandLeft, bandTop);
            }
        }
    };

    walk(root, 0, 0);

    if (model.elements.length === 0) {
        return { ok: false, reason: 'No editable objects found (memo/barcode/shape/picture)' };
    }

    const handle = { doc, nodes, decimalSep, prolog, dpi };

    return {
        ok: true,
        handle,
        model,
        lanes,
        isFlat: dataBands === 0,
        warnings,
    };
}

/** Format a FastReport unit value with the file's own decimal separator. */
function fmtUnits(value, sep) {
    let out = (Math.round(value * 100) / 100).toString();
    if (sep === ',') out = out.replace('.', ',');
    return out;
}

/**
 * Apply the editor's element state back onto the DOM — only what changed.
 * Elements missing from `elements` (deleted in the editor) have their nodes
 * removed. Returns the number of touched nodes.
 */
export function applyFr3Edits(handle, elements) {
    const seen = {};
    let touched = 0;

    elements.forEach((el) => {
        const entry = handle.nodes[el.id];
        if (!entry) return;
        seen[el.id] = true;
        const { node, originLeft, originTop, orig } = entry;
        const sep = handle.decimalSep;

        const newLeftU = mmToUnits(el.x) - originLeft;
        const newTopU = mmToUnits(el.y) - originTop;
        const newWidthU = mmToUnits(el.w || 0);
        const newHeightU = mmToUnits(el.h || 0);
        let changed = false;

        if (Math.abs(newLeftU - orig.leftU) > 0.05) { node.setAttribute('Left', fmtUnits(newLeftU, sep)); changed = true; }
        if (Math.abs(newTopU - orig.topU) > 0.05) { node.setAttribute('Top', fmtUnits(newTopU, sep)); changed = true; }
        if (Math.abs(newWidthU - orig.widthU) > 0.05) { node.setAttribute('Width', fmtUnits(newWidthU, sep)); changed = true; }
        if (Math.abs(newHeightU - orig.heightU) > 0.05) { node.setAttribute('Height', fmtUnits(newHeightU, sep)); changed = true; }

        if (el.type === 'text') {
            if (orig.text !== null && el.text !== orig.text) {
                node.setAttribute('Text', el.text);
                changed = true;
            }
            const newFontU = mmToUnitsFromDots(el.font || 24, handle.dpi);
            if (Math.abs(newFontU - orig.fontU) > 0.5) {
                node.setAttribute('Font.Height', String(Math.round(newFontU) * orig.fontSign));
                changed = true;
            }
            const alignAttr = el.align === 'C' ? 'haCenter' : (el.align === 'R' ? 'haRight' : (el.align === 'J' ? 'haBlock' : 'haLeft'));
            const origAlign = orig.hAlign === '' ? 'haLeft' : orig.hAlign;
            if (alignAttr !== origAlign) {
                node.setAttribute('HAlign', alignAttr);
                changed = true;
            }
        }

        if (changed) touched += 1;
    });

    // Deleted in the editor -> removed from the document.
    Object.keys(handle.nodes).forEach((id) => {
        if (!seen[id]) {
            const entry = handle.nodes[id];
            if (entry.node.parentNode) {
                entry.node.parentNode.removeChild(entry.node);
                touched += 1;
            }
        }
    });

    return touched;
}

/**
 * Serialise the document: original prolog + ASCII-only body (non-ASCII as
 * numeric character references, valid under any declared encoding).
 */
export function serializeFr3(handle) {
    let body = new XMLSerializer().serializeToString(handle.doc.documentElement);
    // eslint-disable-next-line no-control-regex
    body = body.replace(/[\u0080-\uFFFF]/g, (ch) => '&#' + ch.codePointAt(0) + ';');
    return handle.prolog + '\n' + body + '\n';
}

/** utf-8 safe base64 for the ASCII serialisation (it IS ascii, but be safe). */
export function encodeBase64(text) {
    const bytes = new TextEncoder().encode(text);
    let binary = '';
    for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
    return btoa(binary);
}
